import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getCustomerAccounts } from 'app/entities/customer-account/customer-account.reducer';
import { getEntities as getOrganisations } from 'app/entities/organisation/organisation.reducer';
import { getEntities as getSuppliers } from 'app/entities/supplier/supplier.reducer';
import { CustomerType } from 'app/shared/model/enumerations/customer-type.model';
import { CustomerStatus } from 'app/shared/model/enumerations/customer-status.model';
import { SendToMandateStatus } from 'app/shared/model/enumerations/send-to-mandate-status.model';
import { createEntity, getEntity, reset, updateEntity } from './customer.reducer';

export const CustomerUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const customerAccounts = useAppSelector(state => state.customerAccount.entities);
  const organisations = useAppSelector(state => state.organisation.entities);
  const suppliers = useAppSelector(state => state.supplier.entities);
  const customerEntity = useAppSelector(state => state.customer.entity);
  const loading = useAppSelector(state => state.customer.loading);
  const updating = useAppSelector(state => state.customer.updating);
  const updateSuccess = useAppSelector(state => state.customer.updateSuccess);
  const customerTypeValues = Object.keys(CustomerType);
  const customerStatusValues = Object.keys(CustomerStatus);
  const sendToMandateStatusValues = Object.keys(SendToMandateStatus);

  const handleClose = () => {
    navigate('/customer');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getCustomerAccounts({}));
    dispatch(getOrganisations({}));
    dispatch(getSuppliers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.createdTimestamp = convertDateTimeToServer(values.createdTimestamp);

    const entity = {
      ...customerEntity,
      ...values,
      customerAccount: customerAccounts.find(it => it.id.toString() === values.customerAccount?.toString()),
      organisation: organisations.find(it => it.id.toString() === values.organisation?.toString()),
      suppliers: mapIdList(values.suppliers),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdTimestamp: displayDefaultDateTime(),
        }
      : {
          customerType: 'B2B',
          customerStatus: 'PROSPECT',
          sendToMandate: 'INIT',
          ...customerEntity,
          createdTimestamp: convertDateTimeFromServer(customerEntity.createdTimestamp),
          customerAccount: customerEntity?.customerAccount?.id,
          organisation: customerEntity?.organisation?.id,
          suppliers: customerEntity?.suppliers?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.customer.home.createOrEditLabel" data-cy="CustomerCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.customer.home.createOrEditLabel">
              Create or edit a Customer
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="customer-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.customer.customerType')}
                id="customer-customerType"
                name="customerType"
                data-cy="customerType"
                type="select"
              >
                {customerTypeValues.map(customerType => (
                  <option value={customerType} key={customerType}>
                    {translate(`crawlerBackendApp.CustomerType.${customerType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.customer.customerStatus')}
                id="customer-customerStatus"
                name="customerStatus"
                data-cy="customerStatus"
                type="select"
              >
                {customerStatusValues.map(customerStatus => (
                  <option value={customerStatus} key={customerStatus}>
                    {translate(`crawlerBackendApp.CustomerStatus.${customerStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.customer.sendToMandate')}
                id="customer-sendToMandate"
                name="sendToMandate"
                data-cy="sendToMandate"
                type="select"
              >
                {sendToMandateStatusValues.map(sendToMandateStatus => (
                  <option value={sendToMandateStatus} key={sendToMandateStatus}>
                    {translate(`crawlerBackendApp.SendToMandateStatus.${sendToMandateStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.customer.createdTimestamp')}
                id="customer-createdTimestamp"
                name="createdTimestamp"
                data-cy="createdTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="customer-customerAccount"
                name="customerAccount"
                data-cy="customerAccount"
                label={translate('crawlerBackendApp.customer.customerAccount')}
                type="select"
              >
                <option value="" key="0" />
                {customerAccounts
                  ? customerAccounts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="customer-organisation"
                name="organisation"
                data-cy="organisation"
                label={translate('crawlerBackendApp.customer.organisation')}
                type="select"
              >
                <option value="" key="0" />
                {organisations
                  ? organisations.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.customer.suppliers')}
                id="customer-suppliers"
                data-cy="suppliers"
                type="select"
                multiple
                name="suppliers"
              >
                <option value="" key="0" />
                {suppliers
                  ? suppliers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/customer"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CustomerUpdate;
